import { Injectable } from '@angular/core';
import { CleverTapService } from './clevertap.service';
import { FirebaseAnalyticsService } from './firebase-analytics.service';
import { User } from '@app/pages/shared/interfaces/user.model';
import {
  AnalyticsProfile,
  UserProfile,
} from '@app/pages/shared/interfaces/viewer-profile.interface';
import { LocalStorageService } from '../local-storage.service';
import { AppConstant } from '@app/core/constant/app-constant';
import { EventType } from '@app/core/enums';
import {
  EventParams,
  FailureEventParams,
  AnalyticsPreferencesPayload,
} from '@app/core/interfaces';

@Injectable({
  providedIn: 'root',
})
export class Analytics {
  private readonly user: User | null = null;
  private eventTypeLabel = 'eventType';
  private userIdLabel = 'userId';
  private roleLabel = 'role';

  private readonly isCleverTapEnabled =
    AppConstant.EnabledAnalyticsServices.clevertap;
  private readonly isGoogleAnalyticsEnabled =
    AppConstant.EnabledAnalyticsServices.googleAnalytics;

  constructor(
    private readonly clevertap: CleverTapService,
    private readonly googleAnalytics: FirebaseAnalyticsService,
    public localStorageService: LocalStorageService
  ) {
    this.user = JSON.parse(this.localStorageService.get('_user'));
  }

  initCleverTap() {
    if (this.isCleverTapEnabled) this.clevertap.initializeCleverTap();
  }
  addExtraEventParams(params: EventParams, type?: EventType) {
    params[this.eventTypeLabel] = type ?? params.eventType; // Ensure eventType is always set

    if (!params[this.userIdLabel] && this.user?._id) {
      params[this.userIdLabel] = this.user._id ?? null;
      params[this.roleLabel] = this.user?.role ?? null;
    }

    return params;
  }

  dialogOpen(popupName: string, params?: EventParams) {
    if (params) params = this.addExtraEventParams(params, EventType.dialog);
    else {
      params = { eventType: EventType.dialog, page: popupName };
    }
    if (this.isGoogleAnalyticsEnabled) {
      this.googleAnalytics.logDialogOpen(popupName, params);
    }
    // for future implementation if needed
    // if (this.isCleverTapEnabled) {
    //   this.clevertap.pageOpenEvent(params, true);
    // }
  }

  dialogClosed() {
    if (this.isGoogleAnalyticsEnabled) {
      this.googleAnalytics.logDialogClosed();
    }
    // for future implementation if needed
    // if (this.isCleverTapEnabled) {
    //   this.clevertap.pageExit(true);
    // }
  }

  pageOpen(eventName: string, params?: EventParams) {
    if (params) params = this.addExtraEventParams(params, EventType.page);
    else {
      params = { eventType: EventType.page, page: `${eventName}` };
    }
    // for future implementation if needed
    // if (this.isCleverTapEnabled) {
    //   this.clevertap.pageOpenEvent(params, false);
    // }
    if (this.isGoogleAnalyticsEnabled)
      this.googleAnalytics.logPageOpen(eventName, params);
  }

  pageExit() {
    if (this.isGoogleAnalyticsEnabled) this.googleAnalytics.logPageExit();
    // for future implementation if needed
    // if (this.isCleverTapEnabled) this.clevertap.pageExit(false);
  }

  /**
   * Logs login analytics. When CleverTap is enabled, initializes CleverTap first
   * and then sends the login event so the event is always logged after init.
   */
  loginAnalytics(
    params: User,
    preferences: AnalyticsPreferencesPayload,
    countryCode: string
  ) {
    const userData: AnalyticsProfile = {
      _id: params._id,
      name: params.firstName ? `${params.firstName} ${params.lastName}` : '',
      email: params.email ? params.email : '',
      phone: params.phoneNumber ? `${countryCode}${params.phoneNumber}` : '',
      userRole: params.role,
      isEmailVerified: params.isEmailVerified,
      isPhoneVerified: params.isPhoneVerified,
      userPreference: {
        ...preferences,
      },
      appLanguage: params.defaultLanguage,
      isWhatsApp: params.isWaNumber,
    };

    if (this.isGoogleAnalyticsEnabled) this.googleAnalytics.onLogin(userData);

    if (this.isCleverTapEnabled) {
      this.initCleverTap();
      this.clevertap.onLogin(userData);
    }
  }

  updateAnalyticsProfile(params: UserProfile, page: string) {
    let userData: AnalyticsProfile = {
      name: `${params.firstname} ${params.lastname}`,
      email: params.email,
      phone: `+${params.phoneNumber.dialCode}${params.phoneNumber.number}`,
      userRole: params.role,
      country: params.countryCode,
      dob: params.dateOfBirth,
      isEmailVerified: params.isEmailVerified,
      isPhoneVerified: params.isPhoneVerified,
      appLanguage: params.defaultLanguage,
      isWhatsApp: params.isWaNumber,
    };

    if (this.isGoogleAnalyticsEnabled) {
      this.googleAnalytics.onUpdateProfile(userData);
    }
    if (this.isCleverTapEnabled) {
      this.clevertap.onUpdateProfile(userData);
    }
  }

  preferencesUpdated(preferences: AnalyticsPreferencesPayload, page: string) {
    if (this.isCleverTapEnabled)
      this.clevertap.onPreferencesUpdate(preferences, page);
    if (this.isGoogleAnalyticsEnabled) {
      this.googleAnalytics.logCustomEvent('Preferences Update Success', {
        page: page,
        eventType: EventType.api,
        ...preferences,
      });
    }
  }

  private readonly getClevertapGeneralEvents = (
    type: EventType | undefined,
    actualEvent: string,
    params: EventParams
  ) => {
    switch (type) {
      case EventType.api:
        return {
          name: 'API Success',
          isShow: false,
          parameters: { ...params },
        };
      case EventType.dialog:
        return { name: 'Dialog Open', isShow: true, parameters: { ...params } };
      case EventType.page:
        return { name: 'Page Open', isShow: true, parameters: { ...params } };
      case EventType.apiError:
        return {
          name: 'API Failure',
          isShow: true,
          parameters: { name: actualEvent, ...params },
        };
      case EventType.generalError:
        return {
          name: 'Error',
          isShow: true,
          parameters: { name: actualEvent, ...params },
        };
      case EventType.error:
        return { name: actualEvent, isShow: true, parameters: { ...params } };
      case EventType.click:
        return {
          name: 'Button Click',
          isShow: true,
          parameters: { button: actualEvent, ...params },
        };
      case EventType.log:
        return { name: 'Log', isShow: false, parameters: { ...params } };
      default:
        return { name: actualEvent, isShow: true, parameters: { ...params } };
    }
  };

  customAnalytics(eventName: string, eventParams: EventParams) {
    eventParams = this.addExtraEventParams(eventParams, eventParams.eventType);
    if (this.isGoogleAnalyticsEnabled)
      this.googleAnalytics.logCustomEvent(eventName, eventParams);
    if (this.isCleverTapEnabled) {
      let event = this.getClevertapGeneralEvents(
        eventParams?.eventType,
        eventName,
        eventParams
      );
      if (event.isShow)
        this.clevertap.customEvent(event.name, event.parameters);
    }
  }

  failureEvent(eventName: string, eventParams: FailureEventParams) {
    const completeEventParams: EventParams = {
      ...eventParams,
      eventType: eventParams?.eventType ?? EventType.apiError,
      page: eventParams.page,
      reason: eventParams.reason ?? eventParams.error?.message ?? '',
    };

    const finalParams = {
      ...completeEventParams,
      ...this.addExtraEventParams(completeEventParams),
    };

    if (this.isGoogleAnalyticsEnabled) {
      this.googleAnalytics.logAPIFailure(eventName, finalParams);
    }

    if (this.isCleverTapEnabled) {
      let event = this.getClevertapGeneralEvents(
        finalParams?.eventType,
        eventName,
        finalParams
      );
      if (event.isShow)
        this.clevertap.failureEvent(event.name, event.parameters);
    }
  }
}
